import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock3, Home, KeyRound, LogOut, Menu, Palette, X, BarChart3, Trophy, WalletCards, Filter } from 'lucide-react';
import { logout, stopImpersonation } from '../api/management';
import { Button } from '../components/ui/Button';
import { useAdminSession } from '../auth/AdminSessionGuard';

type AdminTheme = 'midnight' | 'graphite' | 'navy' | 'emerald' | 'amethyst' | 'ocean' | 'teal' | 'carbon' | 'cobalt' | 'pearl-light' | 'sky-light' | 'ivory-light' | 'sand-light' | 'lavender-light';

const THEME_KEY = 'ssv2.admin.theme.v1';
const themes: Array<{ id: AdminTheme; label: string; swatch: string }> = [
  { id: 'midnight', label: 'Midnight', swatch: '#132033' },
  { id: 'graphite', label: 'Graphite', swatch: '#20252D' },
  { id: 'navy', label: 'Navy', swatch: '#10213D' },
  { id: 'emerald', label: 'Emerald', swatch: '#102922' },
  { id: 'amethyst', label: 'Amethyst', swatch: '#241B31' },
  { id: 'ocean', label: 'Ocean', swatch: '#0D2637' },
  { id: 'teal', label: 'Teal', swatch: '#0E2B2D' },
  { id: 'carbon', label: 'Carbon', swatch: '#181B1F' },
  { id: 'cobalt', label: 'Cobalt', swatch: '#14264A' },
  { id: 'pearl-light', label: 'Pearl Light', swatch: '#F7F8FA' },
  { id: 'sky-light', label: 'Sky Light', swatch: '#EAF4FB' },
  { id: 'ivory-light', label: 'Ivory Light', swatch: '#F7F4ED' },
  { id: 'sand-light', label: 'Sand Light', swatch: '#F3EEE5' },
  { id: 'lavender-light', label: 'Lavender Light', swatch: '#F2EEF7' },
];

function getInitialTheme(): AdminTheme {
  try {
    const saved = localStorage.getItem(THEME_KEY) as AdminTheme | null;
    return themes.some((option) => option.id === saved) ? saved as AdminTheme : 'midnight';
  } catch {
    return 'midnight';
  }
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const { session } = useAdminSession();
  const isAdmin = session.user.role === 'ADMIN';
  const panelLabel = session.user.role === 'ADMIN' ? 'Admin Panel' : session.user.role === 'SUPER_MASTER' ? 'Super Master Panel' : session.user.role === 'MASTER' ? 'Master Panel' : 'Distributor Panel';
  const roleLabel = session.user.role === 'ADMIN' ? 'Administration' : session.user.role.replace('_', ' ');
  const actorLabel = session.user.displayName || session.user.username;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [returning, setReturning] = useState(false);
  const [theme, setTheme] = useState<AdminTheme>(getInitialTheme);
  const [themeOpen, setThemeOpen] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.documentElement.dataset.adminTheme = theme;
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* local storage can be unavailable */ }
    return () => { delete document.documentElement.dataset.adminTheme; };
  }, [theme]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!themeMenuRef.current?.contains(event.target as Node)) setThemeOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const doReturn = async () => {
    if (!session.impersonating || returning) return;
    setReturning(true);
    try {
      await stopImpersonation();
      window.location.assign('/');
    } finally {
      setReturning(false);
    }
  };

  const doLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate('/login/', { replace: true });
    }
  };

  return (
    <div className="admin-theme-shell min-h-screen">
      {sidebarOpen ? <button type="button" className="fixed inset-0 z-30 bg-black/55 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close navigation" /> : null}

      <aside className={`admin-sidebar fixed inset-y-0 left-0 z-40 w-72 border-r shadow-[8px_0_30px_rgba(0,0,0,0.35)] backdrop-blur-md transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="admin-border flex h-20 items-center justify-between border-b px-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-300">Super Sunday V2</p>
            <p className="admin-text-strong mt-1 font-bold">{panelLabel}</p>
          </div>
          <button type="button" className="admin-ghost rounded-lg p-2 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar"><X size={18} aria-hidden="true" /></button>
        </div>

        <nav className="space-y-2 p-4" aria-label="Admin navigation">
          <button type="button" onClick={() => { navigate('/'); setSidebarOpen(false); }} className="crystal-btn crystal-sapphire flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Dashboard</span><Home size={16} aria-hidden="true" /></button>
          {isAdmin ? <button type="button" onClick={() => { navigate('/draw-time/'); setSidebarOpen(false); }} className="crystal-btn crystal-violet flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Draw Time</span><Clock3 size={16} aria-hidden="true" /></button> : null}
          {isAdmin ? <button type="button" onClick={() => { navigate('/draw-result/'); setSidebarOpen(false); }} className="crystal-btn crystal-sapphire flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Draw Result</span><Trophy size={16} aria-hidden="true" /></button> : null}
          {isAdmin ? <button type="button" onClick={() => { navigate('/balance-update/'); setSidebarOpen(false); }} className="crystal-btn crystal-emerald flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Balance Update</span><WalletCards size={16} aria-hidden="true" /></button> : null}
          {isAdmin ? <button type="button" onClick={() => { navigate('/data-filter/'); setSidebarOpen(false); }} className="crystal-btn crystal-sapphire flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Data Filter</span><Filter size={16} aria-hidden="true" /></button> : null}
          <button type="button" onClick={() => { navigate('/hierarchy-reports/'); setSidebarOpen(false); }} className="crystal-btn crystal-emerald flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Reports</span><BarChart3 size={16} aria-hidden="true" /></button>
          {!isAdmin ? <button type="button" onClick={() => { navigate('/payment-details/?source=upline&view=book-details'); setSidebarOpen(false); }} className="crystal-btn crystal-sapphire flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black"><span>Book Details</span><BookOpen size={16} aria-hidden="true" /></button> : null}
          <div className="admin-border mt-4 grid gap-2 border-t pt-4">
            <Button variant="secondary" className="w-full" icon={<KeyRound size={15} aria-hidden="true" />} onClick={() => { navigate('/change-password/'); setSidebarOpen(false); }}>Change Password</Button>
            <Button variant="danger" className="w-full" icon={<LogOut size={15} aria-hidden="true" />} loading={loggingOut} onClick={() => void doLogout()}>Logout</Button>
          </div>
        </nav>
      </aside>

      <div className="lg:pl-72">
        <header className="admin-header sticky top-0 z-20 flex h-20 items-center justify-between border-b px-4 shadow-[0_6px_18px_rgba(0,0,0,0.20)] backdrop-blur-md sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button type="button" className="admin-menu-button grid h-11 w-11 shrink-0 place-items-center rounded-xl lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Open navigation"><Menu size={20} aria-hidden="true" /></button>
            <div className="min-w-0">
              <p className="admin-text-muted truncate text-[10px] font-bold uppercase tracking-wide sm:text-xs">{roleLabel}</p>
              <p className="admin-text-strong truncate text-xs font-black sm:text-sm">{actorLabel}</p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {session.impersonating ? <button type="button" onClick={() => void doReturn()} disabled={returning} className="crystal-btn crystal-smoky grid h-9 w-9 place-items-center rounded-lg p-0 disabled:cursor-not-allowed disabled:opacity-50" aria-label={`Return to ${session.ownerDisplayName || 'parent account'}`} title="Return"><ArrowLeft size={16} aria-hidden="true" /></button> : null}
            <button type="button" onClick={() => navigate('/')} className="admin-menu-button grid h-9 w-9 place-items-center rounded-lg" aria-label="Home" title="Home"><Home size={16} aria-hidden="true" /></button>
            <div ref={themeMenuRef} className="relative">
              <button type="button" onClick={() => setThemeOpen((open) => !open)} className="admin-theme-trigger crystal-btn crystal-smoky inline-flex h-9 items-center justify-center rounded-lg px-2.5 py-1 text-[10px] font-black sm:text-[11px]" aria-haspopup="menu" aria-expanded={themeOpen}><span className="inline-flex items-center gap-1"><Palette size={13} aria-hidden="true" />Theme</span></button>
              {themeOpen ? (
                <div className="admin-theme-picker absolute right-0 top-[calc(100%+8px)] z-50 w-44 rounded-2xl p-2 shadow-[0_18px_44px_rgba(0,0,0,0.28)]" role="menu">
                  {themes.map((option) => (
                    <button key={option.id} type="button" role="menuitemradio" aria-checked={theme === option.id} onClick={() => { setTheme(option.id); setThemeOpen(false); }} className={`admin-theme-option flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs font-black ${theme === option.id ? 'is-active' : ''}`}>
                      <span className="h-4 w-4 rounded-full border border-white/20" style={{ background: option.swatch }} />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button type="button" onClick={() => void doLogout()} disabled={loggingOut} className="crystal-btn crystal-ruby grid h-9 w-9 place-items-center rounded-lg p-0 text-white disabled:cursor-not-allowed disabled:opacity-50" aria-label="Logout" title="Logout"><LogOut size={16} aria-hidden="true" /></button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
